import { useCallback, useEffect, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsService } from '../services/insights'
import { DomainService } from '../services/domain'
import { AppState } from '../stores/app'
import { observe } from 'mobx'
import dayjs from 'dayjs'
import { ReportEntity } from '../features/reporting_v2/enums/report-entity'
import { ReportDateIncrement } from '../features/reporting_v2/enums/report-date-increment'

export interface KPIMetric {
    label: string
    value: number
    formattedValue: string
    trend?: 'up' | 'down' | 'flat'
    trendPercent?: number
    /** Daily values for sparkline rendering (oldest → newest) */
    sparkData?: number[]
    /** Secondary display value (e.g., "+12,450 new") */
    secondaryValue?: string
    /** Whether this is a rate metric (uses absolute point change for delta) */
    isRate?: boolean
}

interface UseKPIMetricsResult {
    data: KPIMetric[] | null
    loading: boolean
    error: Error | null
    /** Timestamp of last successful fetch */
    lastUpdated: Date | null
    refresh: () => void
}

function formatNumber(value: number): string {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1)}M`
    }
    if (value >= 10_000) {
        return `${(value / 1_000).toFixed(1)}k`
    }
    return value.toLocaleString()
}

function formatPercent(value: number): string {
    if (value === 0) return '0%'
    return `${(value * 100).toFixed(2)}%`
}

function computeTrend(current: number, previous: number): { trend: 'up' | 'down' | 'flat'; trendPercent: number } {
    if (previous === 0) {
        if (current === 0) return { trend: 'flat', trendPercent: 0 }
        return { trend: 'up', trendPercent: 100 }
    }
    const percent = ((current - previous) / previous) * 100
    const rounded = Math.round(percent * 10) / 10
    if (rounded > 0) return { trend: 'up', trendPercent: rounded }
    if (rounded < 0) return { trend: 'down', trendPercent: Math.abs(rounded) }
    return { trend: 'flat', trendPercent: 0 }
}

export function useKPIMetrics(): UseKPIMetricsResult {
    const insightsService = useRef(Container.get(InsightsService)).current
    const domainService = useRef(Container.get(DomainService)).current
    const appState = useRef(Container.get(AppState)).current

    const [data, setData] = useState<KPIMetric[] | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

    const fetchMetrics = useCallback(async () => {
        const domainId = appState.currentDomain?.id
        if (!domainId) return

        setLoading(true)
        setError(null)

        try {
            const yesterdayDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD')
            const dayBeforeDate = dayjs().subtract(2, 'day').format('YYYY-MM-DD')
            const todayDate = dayjs().format('YYYY-MM-DD')

            // Fetch all 3 in parallel:
            // 1. Current total subscriber estimate
            // 2. Yesterday's new subscriptions (from prompts entity) — for subscriber sparkline
            // 3. Yesterday's + day-before clicks/CTR (from notifications entity)
            const [subscriberEstimate, subsStats, deliveryStats] = await Promise.all([
                // 1. Total active subscribers NOW
                domainService.fetchActiveSubscriberEstimateForDomainId(domainId, 'count', {
                    cancellationKey: 'kpi-metrics.subs-estimate',
                }),

                // 2. New subscriptions for last 7 days (for sparkline + yesterday delta)
                insightsService.fetch(
                    {
                        entity: ReportEntity.PROMPTS,
                        date_increment: ReportDateIncrement.DAY,
                        date_range: {
                            since: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
                            through: todayDate,
                        },
                        breakdowns: ['domain'],
                        fields: ['subscriptions'],
                        filters: [{ field: 'domain.id', operator: 'eq', value: domainId }],
                        order: { field: 'event_date', direction: 'asc' },
                    },
                    {
                        cancellationKey: 'kpi-metrics.subs-stats',
                        scopedInsightsPath: 'insights',
                    },
                ),

                // 3. Clicks/CTR for yesterday + day before (for delta)
                insightsService.fetch(
                    {
                        entity: ReportEntity.NOTIFICATIONS,
                        date_increment: ReportDateIncrement.DAY,
                        date_range: {
                            since: dayBeforeDate,
                            through: todayDate,
                        },
                        breakdowns: ['domain'],
                        fields: ['clicks', 'ctr_decimal'],
                        filters: [{ field: 'domain.id', operator: 'eq', value: domainId }],
                        order: { field: 'send_date', direction: 'asc' },
                    },
                    {
                        cancellationKey: 'kpi-metrics.delivery-stats',
                        scopedInsightsPath: 'insights',
                    },
                ),
            ])

            // --- Push Subscribers ---
            const totalSubs = (subscriberEstimate as any)?.reach ?? 0

            // Sparkline: daily new subscriptions over last 7 days
            const subsRows = Array.isArray(subsStats) ? subsStats : []
            const subsSpark = subsRows.map((r: any) => r.subscriptions || 0)

            // Delta: yesterday's new subs vs day-before's new subs
            const subsYesterday = subsRows.find((r: any) => r.event_date === yesterdayDate)
            const subsDayBefore = subsRows.find((r: any) => r.event_date === dayBeforeDate)
            const newSubsYesterday = subsYesterday?.subscriptions ?? 0
            const newSubsDayBefore = subsDayBefore?.subscriptions ?? 0
            const subsTrend = computeTrend(newSubsYesterday, newSubsDayBefore)

            // --- Clicks & CTR ---
            // Show yesterday's values with delta vs day-before-yesterday (matches desktop)
            const deliveryRows = Array.isArray(deliveryStats) ? deliveryStats : []
            const deliveryYesterday = deliveryRows.find((r: any) => r.send_date === yesterdayDate)
            const deliveryDayBefore = deliveryRows.find((r: any) => r.send_date === dayBeforeDate)

            const clicksYesterday = deliveryYesterday?.clicks ?? 0
            const clicksDayBefore = deliveryDayBefore?.clicks ?? 0
            const clicksTrend = computeTrend(clicksYesterday, clicksDayBefore)

            const ctrYesterday = deliveryYesterday?.ctr_decimal ?? 0
            const ctrDayBefore = deliveryDayBefore?.ctr_decimal ?? 0
            // For rate metrics (CTR), use absolute point change instead of relative %
            const ctrDiffPts = (ctrYesterday - ctrDayBefore) * 100
            const ctrTrend: { trend: 'up' | 'down' | 'flat'; trendPercent: number } = {
                trend: ctrDiffPts > 0.05 ? 'up' : ctrDiffPts < -0.05 ? 'down' : 'flat',
                trendPercent: Math.round(Math.abs(ctrDiffPts) * 100) / 100,
            }

            const metrics: KPIMetric[] = [
                {
                    label: 'Push Subscribers',
                    value: totalSubs,
                    formattedValue: totalSubs ? `~${formatNumber(totalSubs)}` : '--',
                    trend: subsTrend.trend,
                    trendPercent: subsTrend.trendPercent,
                    sparkData: subsSpark,
                    secondaryValue:
                        newSubsYesterday > 0 ? `+${formatNumber(newSubsYesterday)} new yesterday` : undefined,
                },
                {
                    label: 'Clicks',
                    value: clicksYesterday,
                    formattedValue: clicksYesterday > 0 ? formatNumber(clicksYesterday) : '--',
                    trend: clicksTrend.trend,
                    trendPercent: clicksTrend.trendPercent,
                },
                {
                    label: 'CTR',
                    value: ctrYesterday,
                    formattedValue: ctrYesterday > 0 ? formatPercent(ctrYesterday) : '--',
                    trend: ctrTrend.trend,
                    trendPercent: ctrTrend.trendPercent,
                    isRate: true,
                },
            ]

            setData(metrics)
            setLastUpdated(new Date())
            setError(null)
        } catch (err) {
            // Preserve previous data on error
            setError(err instanceof Error ? err : new Error('Failed to fetch KPI metrics'))
        } finally {
            setLoading(false)
        }
    }, [appState, insightsService, domainService])

    // Fetch on mount and when domain changes
    useEffect(() => {
        fetchMetrics()

        const dispose = observe(appState, 'currentDomainJsonData', () => {
            // Clear stale data immediately so skeleton states show
            setData(null)
            fetchMetrics()
        })

        return () => dispose()
    }, [fetchMetrics, appState])

    return { data, loading, error, lastUpdated, refresh: fetchMetrics }
}
